import {apiClient, apiStreamQuery, type MerchantConversionRequest, type MerchantScopedRequest} from "../connection/BlinkApiClient";
import {Money} from "../../stem-core/localization/Money";
import {RecurringPaymentStoreObject} from "./RecurringPaymentStoreObject";
import {SubscriptionDiscount} from "./SubscriptionDiscountStore";
import {field} from "../../stem-core/state/StoreField";
import {MerchantUser} from "./merchant/MerchantUserStore";
import {SubscriptionBenefit, serializeBenefitForSDK} from "./SubscriptionBenefitStore";
import {BaseStore, globalStore} from "../../stem-core/state/Store";
import {UserProfile} from "./UserProfileStore";
import {BillingPlan, SubscriptionOffer, type CalculatedSubscriptionPrices, type SubscriptionPriceBreakdown} from "./SubscriptionOfferStore";
import {SubscriptionCoverage} from "./SubscriptionCoverageStore";
import {type StoreId} from "../../stem-core/state/State";
import type {StemDate} from "../../stem-core/time/Date";
import {UserAddress} from "./UserAddressStore";

export const SubscriptionApplyPolicy = {
    INSTANTLY: "instantly",
    NEXT_BILLING_CYCLE: "next_billing_cycle",
} as const;

// SubscriptionChangeApplyPolicyEnum on the backend
export type SubscriptionApplyPolicyType = typeof SubscriptionApplyPolicy[keyof typeof SubscriptionApplyPolicy];


@globalStore
export class Subscription extends BaseStore(
    "Subscription",
    {dependencies: ["Currency", "Merchant", "SubscriptionCoverage", "SubscriptionOffer", "BillingPlan", "SubscriptionBenefit"]},
    RecurringPaymentStoreObject
) {
    // Computed and serialized by Subscription.get_next_cycle_price(), which answers null on the last cycle
    declare nextCyclePrice?: SubscriptionPriceBreakdown | null;

    @field("BillingPlan") billingPlan: BillingPlan;
    @field("SubscriptionCoverage") coverage: SubscriptionCoverage;
    @field("SubscriptionOffer") recurringOffer: SubscriptionOffer;
    @field("SubscriptionDiscount") discount: SubscriptionDiscount;
    @field("Subscription") fromGroupSubscription: Subscription;
    @field("UserProfile") billedUser: UserProfile;
    // TODO @Mihai Only for group subscriptions, disabled for now
    @field(Boolean) isGroupSubscription: boolean;
    @field(Array) groupEmailDomains: string[];
    @field(Array) groupIpV4Ranges: string[];
    @field(Number) groupNumCustomBeneficiaries: number;
    @field(Boolean) ownerCanEditIpRanges: boolean;
    @field(Boolean) ownerCanEditDomains: boolean;
    @field(Array) extraBenefitIds: number[];
    @field(Object) lastCyclePrice?: {finalPrice: number}; // The rest of the breakdown is unused here

    getAllBenefitIds(): number[] {
        const benefitIds = new Set<number>([...this.coverage.getFullBenefitIds(), ...(this.extraBenefitIds || [])]);
        if (this.recurringOffer) {
            for (const id of this.recurringOffer.extraBenefitIds) {
                benefitIds.add(id);
            }
        }
        return Array.from(benefitIds);
    }

    getOwnExtraBenefits(): SubscriptionBenefit[] {
        return (this.extraBenefitIds || []).map(id => SubscriptionBenefit.get(id)).filter(benefit => benefit);
    }

    async reactivate(): Promise<any> {
       return apiReactivateSubscription(this.id);
    }

    async updatePaymentMethod(paymentMethodId: StoreId): Promise<any> {
        return apiUpdateSubscription({
            subscriptionId: this.id,
            preferredPaymentMethodId: paymentMethodId,
        });
    }

    getMerchantUser(): MerchantUser | null {
        return MerchantUser.getByUserAndMerchant(this.userId, this.merchantId);
    }

    // TODO Deprecate this field
    getDiscount(): SubscriptionDiscount | null {
        return this.discount;
    }

    getPrice(): Money | null {
        return Money.optionally(this.lastCyclePrice?.finalPrice, this.getCurrency());
    }

    serializeForSDK(): any {
        // TODO migrate `amount` + `currencyCode` to Money.toRichJSON() once merchants have been notified.
        const {id, deliveryAddressId, blinkSignature, nextPaymentAttempt, userId, amount, createdAt, canceledAt, activeUntil, billingPlanId} = this;
        let obj: Record<string, any> = {id, deliveryAddressId, blinkSignature, nextPaymentAttempt, userId, amount, createdAt, canceledAt, activeUntil, billingPlanId};
        obj.currencyCode = this.getCurrency().getIsoCode();
        obj.name = this.coverage.getName();
        obj.billingPlan = this.billingPlan.serializeForSDK();
        obj.benefits = this.getAllBenefitIds().map(serializeBenefitForSDK);
        return obj;
    }

    formatPriceShortened(): string {
        return `${this.getPrice()}/${this.billingPlan.getAbbreviatedCycleDuration()}`;
    }

    formatPrice(): string {
        return `${this.getPrice()}/${this.billingPlan.getCycleDuration(true)}`;
    }

    static getActive(): Subscription[] {
        return this.filter(subscription => subscription.isActive());
    }

    static getUnpaid(): Subscription[] {
        return this.filter(subscription => subscription.isUnpaid());
    }

    static getActiveAndNotCanceled(): Subscription[] {
        return this.filter(subscription => subscription.status === this.Status.ACTIVE);
    }

    static getPast(): Subscription[] {
        return this.filter(subscription => !subscription.isActive() && !subscription.isUnpaid());
    }

    static getActiveWithAddress(address: UserAddress): Subscription[] {
        return this.getActive().filter(sub => sub.getShippingAddress() === address);
    }
}

// The payload SubscribeRequest validates (api/subscriptions/views.py). merchantId and the two journey
// fields ride in from MerchantConversionRequest. Ids are StoreId rather than the backend's int: that is
// what the frontend holds, and pydantic coerces a numeric string
export interface SubscribeRequest extends MerchantConversionRequest {
    recurringOfferId: StoreId;
    // Both default server-side, to false and ""
    gift?: boolean;
    giftBeneficiaryEmailAddress?: string;
    deliveryAddressId?: StoreId;
    discountCode?: string;
    preferredPaymentMethodId?: StoreId;
    autorenew?: boolean;
}

export async function apiSubscribe(payload: SubscribeRequest): Promise<Subscription | undefined> {
    const response = await apiClient.post("/subscriptions/subscribe", payload);
    return Subscription.load(response).find(subscription => subscription.isActive());
}

// What UpdateSubscriptionRequest validates. applyPolicy is SubscriptionChangeApplyPolicyEnum
export interface UpdateSubscriptionRequest extends MerchantConversionRequest {
    subscriptionId: StoreId;
    deliveryAddressId?: StoreId;
    preferredPaymentMethodId?: StoreId;
    recurringOfferId?: StoreId;
    discountCode?: string;
    applyPolicy?: SubscriptionApplyPolicyType;
}

export async function apiUpdateSubscription(payload: UpdateSubscriptionRequest): Promise<Subscription | undefined> {
    const response = await apiClient.post("/subscriptions/update_subscription", payload);
    return Subscription.load(response).find(subscription => !subscription.isFinished());
}

// Answers with the same shape as the two calculate_subscription_price endpoints in SubscriptionOfferStore
export async function apiRequestSubscriptionPriceOnAddressChange(subscriptionId: StoreId, merchantId: StoreId, deliveryAddressId: StoreId): Promise<CalculatedSubscriptionPrices> {
    return apiClient.get("/subscriptions/calculate_update_subscription_price", {
        subscriptionId,
        merchantId,
        deliveryAddressId,
    });
}

export async function apiCancelSubscription(subscriptionId: StoreId): Promise<any> {
    return apiClient.post("/subscriptions/cancel", {subscriptionId});
}

export async function apiReactivateSubscription(subscriptionId: StoreId): Promise<any> {
    return apiClient.post("/subscriptions/reactivate", {subscriptionId});
}

// What MerchantSubscriptionRequest validates (dashboard/deserializers.py:110). Named for the backend
// class, to keep it apart from the buyer-facing UpdateSubscriptionRequest above
export interface MerchantSubscriptionRequest extends MerchantScopedRequest {
    subscriptionId: StoreId;
}

// What MerchantUpdateSubscriptionRequest validates (dashboard/deserializers.py:118)
export interface MerchantUpdateSubscriptionRequest extends MerchantSubscriptionRequest {
    activeUntil?: StemDate;
    extraBenefitIds?: StoreId[];
}

export async function apiMerchantUpdateSubscription(request: MerchantUpdateSubscriptionRequest): Promise<any> {
    return apiClient.post("/merchant/update_subscription", request);
}

export async function apiMerchantCancelSubscription(request: MerchantSubscriptionRequest): Promise<any> {
    return apiClient.post("/merchant/cancel_subscription", request);
}

export async function apiMerchantCreateGroupSubscription(request: any): Promise<Subscription> {
    const response = await apiClient.post("/merchant/create_group_subscription", request);
    return Subscription.loadObject(response);
}

export async function apiMerchantEditGroupSubscription(request: any): Promise<any> {
    return apiClient.post("/merchant/edit_group_subscription", request);
}

// No handler exists for this path - see the backlog. Typed from what the one caller sends and reads
export async function apiMerchantProcessSubscriptionSentToOffline(request: MerchantSubscriptionRequest): Promise<{isSuccess: boolean; cdsResponse?: any}> {
    return apiClient.post("/external_providers/process_subscription_sent_to_offline", request);
}

export function apiStreamSubscriptions(filters: Record<string, any>): any {
    return apiStreamQuery("/merchant/get_subscriptions", filters);
}

// Returns the subset of the given emails that currently hold an active subscription.
export async function apiGetActiveSubscriberEmails(emails: string[]): Promise<string[]> {
    // dashboard/views.py get_active_subscriber_emails answers {"emails": [...]}
    const response = await apiClient.post<{emails: string[]}>("/merchant/get_active_subscriber_emails", {emails});
    return response.emails;
}
